import {
  booControllerCreateRule,
  booControllerDeleteRule,
  booControllerGetMatrix,
  booControllerListRules,
  booControllerSetOverride,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import type {
  BillOfOperationsMatrixResponse,
  CreateRelevanceRuleInput,
  ProductOperationOverrideInput,
  RelevanceRule,
} from './types';

export const BILL_OF_OPERATIONS_KEY = 'bill_of_operations' as const;

// Cross-feature key used for invalidation when rules change.
// Mirrors `OPERATIONS_KEY` in `features/operations/queries.ts` — kept
// inline rather than imported to avoid a circular module ref between
// the two features' queries files.
const OPERATIONS_KEY = 'operations' as const;

// ─── Matrix query ────────────────────────────────────────────────────

/**
 * Query key for the BoO matrix.
 *
 * The partner-wide view and the per-project view must NOT share a cache
 * entry: the project view fetches a project-scoped, un-truncated response
 * (`project_id` param), whereas the partner-wide view fetches the capped
 * partner response. So the project id is appended when present.
 *
 * The partner-wide key stays the bare `[KEY, 'matrix', partnerId]`, which
 * is a prefix of every project-scoped key — so `invalidateQueries` on the
 * bare key (used by the mutations below) refreshes both views, while the
 * optimistic `get/setQueryData` calls target the exact scoped key.
 */
export function billOfOperationsMatrixQueryKey(partnerId: string | undefined, projectId?: string) {
  return projectId
    ? ([BILL_OF_OPERATIONS_KEY, 'matrix', partnerId, projectId] as const)
    : ([BILL_OF_OPERATIONS_KEY, 'matrix', partnerId] as const);
}

export function billOfOperationsMatrixQueryOptions(
  partnerId: string | undefined,
  projectId?: string,
) {
  return queryOptions({
    queryKey: billOfOperationsMatrixQueryKey(partnerId, projectId),
    queryFn: async ({ signal }) => {
      const res = await booControllerGetMatrix(
        { partner_id: partnerId ?? '', ...(projectId ? { project_id: projectId } : {}) },
        { signal },
      );
      // The generated DTO is structurally identical to the locally-defined
      // `BillOfOperationsMatrixResponse` (which the rest of the feature is
      // typed against); cast at the boundary to keep that single shape.
      return expectData<BillOfOperationsMatrixResponse>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // While any product has an in-flight release, poll the matrix so
    // the "Releasing… N/M" badge advances and clears when the run
    // completes. Otherwise fall back to staleTime caching.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const anyActive = data.products.some((p) => p.active_release_run_id !== null);
      return anyActive ? 2_000 : false;
    },
  });
}

// ─── List rules query (admin only) ───────────────────────────────────

export function relevanceRulesListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [BILL_OF_OPERATIONS_KEY, 'rules-list', partnerId] as const,
    queryFn: async ({ signal }) => {
      const res = await booControllerListRules({ partner_id: partnerId ?? '' }, { signal });
      return expectData<RelevanceRule[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Create rule mutation (admin only) ───────────────────────────────

export function useCreateRelevanceRule(partnerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRelevanceRuleInput) => {
      const res = await booControllerCreateRule(input);
      return expectData<RelevanceRule>(res);
    },
    onSuccess: () => {
      // Both the matrix (cells change) and the rules list (new row) need to refresh.
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix', partnerId],
      });
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'rules-list', partnerId],
      });
      // The operation detail page's Rules section + the BoO usage
      // table's resolved cycle/headcount columns both depend on rules.
      // Invalidate the prefixes so any open operation detail picks up
      // the new rule.
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'rules'] });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'bills-of-operations'] });
    },
  });
}

// ─── Delete rule mutation (admin only) ───────────────────────────────

export function useDeleteRelevanceRule(partnerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      await booControllerDeleteRule(ruleId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix', partnerId],
      });
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'rules-list', partnerId],
      });
      // Mirror create's cross-feature invalidations (see comment there).
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'rules'] });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'bills-of-operations'] });
    },
  });
}

// ─── Set product-operation override mutation ────────────────────────

interface SetOverrideArgs {
  productId: string;
  operationId: string;
  input: ProductOperationOverrideInput;
}

export function useSetProductOperationOverride(partnerId: string | undefined, projectId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, operationId, input }: SetOverrideArgs) => {
      await booControllerSetOverride(productId, operationId, input);
    },
    // Optimistic update: flip the targeted cell immediately so the UI
    // feels instant. On error, roll back via the rollback context; on
    // success, invalidate to re-fetch the authoritative state (which
    // also picks up any materialiser side effects).
    onMutate: async ({ productId, operationId, input }) => {
      const queryKey = billOfOperationsMatrixQueryKey(partnerId, projectId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<BillOfOperationsMatrixResponse>(queryKey);
      if (previous) {
        const exists = previous.cells.some(
          (c) => c.product_id === productId && c.operation_id === operationId,
        );

        let updatedCells: BillOfOperationsMatrixResponse['cells'];
        if (exists) {
          updatedCells = previous.cells.map((c) => {
            if (c.product_id !== productId || c.operation_id !== operationId) return c;
            if (
              input.is_excluded === false &&
              input.cycle_time_override === null &&
              input.headcount_override === null
            ) {
              return {
                ...c,
                applied: true,
                source: 'rule_include' as const,
                cycle_time_override: null,
                headcount_override: null,
                // The override was just touched — mark pending until release.
                is_unreleased: true,
              };
            }
            return {
              ...c,
              applied: input.is_excluded ? false : c.applied,
              source: input.is_excluded ? ('override_exclude' as const) : c.source,
              cycle_time_override: input.cycle_time_override,
              headcount_override: input.headcount_override,
              is_unreleased: true,
            };
          });
        } else {
          // Cell was no_match (not in sparse array) — add it
          updatedCells = [
            ...previous.cells,
            {
              product_id: productId,
              operation_id: operationId,
              applied: !input.is_excluded,
              source: input.is_excluded ? ('override_exclude' as const) : ('rule_include' as const),
              rule_id: null,
              cycle_time_override: input.cycle_time_override,
              headcount_override: input.headcount_override,
              confirmed_workload_category: null,
              suggested_workload_category: 3,
              data_state: 'baseline' as const,
              suggested_data_state: 'baseline' as const,
              is_unreleased: true,
            },
          ];
        }

        queryClient.setQueryData<BillOfOperationsMatrixResponse>(queryKey, {
          ...previous,
          cells: updatedCells,
        });
      }
      return { previous };
    },
    onError: (_err, _args, context) => {
      const queryKey = billOfOperationsMatrixQueryKey(partnerId, projectId);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix', partnerId],
      });
    },
  });
}

// ─── Confirm workload category mutation (ENG-609) ────────────────────

interface ConfirmWorkloadCategoryArgs {
  productId: string;
  operationId: string;
  /** 1..5 to confirm, or null to clear back to Baseline. */
  confirmedCategory: number | null;
  /** Current override values, resent so the upsert preserves them. */
  cycleTimeOverride: string | null;
  headcountOverride: number | null;
}

/**
 * Confirm (or clear) a process workload category on a cell. Hits the same
 * per-product override endpoint as {@link useSetProductOperationOverride}
 * but carries `confirmed_workload_category`; the staged value is captured
 * onto work orders on the next change-control Release. Optimistically flips
 * the cell to Estimated (or back to Baseline) and marks it unreleased.
 */
export function useConfirmWorkloadCategory(partnerId: string | undefined, projectId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      operationId,
      confirmedCategory,
      cycleTimeOverride,
      headcountOverride,
    }: ConfirmWorkloadCategoryArgs) => {
      const input: ProductOperationOverrideInput = {
        is_excluded: false,
        cycle_time_override: cycleTimeOverride,
        headcount_override: headcountOverride,
        confirmed_workload_category: confirmedCategory,
      };
      await booControllerSetOverride(productId, operationId, input);
    },
    onMutate: async ({ productId, operationId, confirmedCategory }) => {
      const queryKey = billOfOperationsMatrixQueryKey(partnerId, projectId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<BillOfOperationsMatrixResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<BillOfOperationsMatrixResponse>(queryKey, {
          ...previous,
          cells: previous.cells.map((c) =>
            c.product_id === productId && c.operation_id === operationId
              ? {
                  ...c,
                  confirmed_workload_category: confirmedCategory,
                  data_state: confirmedCategory !== null && c.applied ? 'estimated' : 'baseline',
                  is_unreleased: true,
                }
              : c,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _args, context) => {
      const queryKey = billOfOperationsMatrixQueryKey(partnerId, projectId);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [BILL_OF_OPERATIONS_KEY, 'matrix', partnerId],
      });
    },
  });
}
